import { useEffect, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";
import Landing from "@/pages/Landing";
import Admin from "@/pages/Admin";
import MemberSpace from "@/pages/MemberSpace";
import RefRedirect from "@/pages/RefRedirect";
import Welcome from "@/pages/Welcome";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Keeps the language in sync with the URL: /fr, /en, /de, /ru, /es are
// self-contained shareable links for each language version of the landing page.
function LangRouteSync() {
  const { pathname } = useLocation();
  const { setLang } = useLang();
  useEffect(() => {
    const m = pathname.match(/^\/(fr|en|de|ru|es|vi|th|hr)(\/|$)/);
    if (m) setLang(m[1]);
  }, [pathname, setLang]);
  return null;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: false };
  }
  static getDerivedStateFromError() {
    return { error: true };
  }
  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            background: "#07090E",
            color: "#94A3B8",
            fontFamily: "Manrope, sans-serif",
          }}
          data-testid="error-boundary"
        >
          Something went wrong. Please refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute("href");
      if (href && href.length > 1) {
        const el = document.querySelector(href);
        if (el) {
          e.preventDefault();
          lenis.scrollTo(el, { offset: -110 });
        }
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      document.removeEventListener("click", onClick);
    };
  }, []);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <BrowserRouter>
          <SmoothScroll />
          <ScrollToTop />
          <LangRouteSync />
          <Routes>
            <Route path="/" element={<Landing />} />
            {Object.keys(translations).map((l) => (
              <Route key={l} path={`/${l}`} element={<Landing />} />
            ))}
            <Route path="/admin" element={<Admin />} />
            <Route path="/membre" element={<MemberSpace />} />
            <Route path="/welcome/:username" element={<Welcome />} />
            <Route path="/r/:username" element={<RefRedirect />} />
            <Route path="*" element={<Landing />} />
          </Routes>
        </BrowserRouter>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
